import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { RoleSelect } from '@/components/ui/RoleSelect'
import { TextField } from '@/components/ui/TextField'
import { createUser } from '@/data/userApi'
import { errorMessage } from '@/lib/errors'
import { ROLE_LABEL, type Role } from '@/types/user'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface CreatedUser {
  uid: string
  name: string
  role: Role
}

interface AddUserModalProps {
  open: boolean
  onClose: () => void
  onCreated: (user: CreatedUser) => void
  /** When set, the role is fixed (used when adding a supplier/client inline). */
  lockedRole?: Role
}

export function AddUserModal({ open, onClose, onCreated, lockedRole }: AddUserModalProps) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<Role>(lockedRole ?? 'supplier')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function reset() {
    setName('')
    setEmail('')
    setPassword('')
    setRole(lockedRole ?? 'supplier')
    setError(null)
  }

  function handleClose() {
    reset()
    onClose()
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    if (!name.trim() || !email.trim() || !password) {
      setError('Name, email and password are required.')
      return
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError('Please enter a valid email address.')
      return
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    setSubmitting(true)
    try {
      const { uid } = await createUser({ name: name.trim(), email: email.trim(), password, role })
      onCreated({ uid, name: name.trim(), role })
      reset()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  const roleWord = lockedRole ? ROLE_LABEL[lockedRole].toLowerCase() : 'user'
  const title = `Add ${roleWord}`
  const submitLabel = `Create ${roleWord}`

  return (
    <Modal open={open} title={title} onClose={handleClose}>
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <TextField
          id="new-user-name"
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoComplete="name"
        />
        <TextField
          id="new-user-email"
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="off"
          placeholder="name@company.com"
        />
        <TextField
          id="new-user-password"
          label="Password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
        />
        {!lockedRole && <RoleSelect id="new-user-role" value={role} onChange={setRole} />}
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Creating…' : submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
